import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orgProfile, setOrgProfile] = useState(null);
  const [assessment, setAssessment] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    const adminToken = localStorage.getItem('adminToken');
    
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data.user);
        setOrgProfile(response.data.org_profile);
        setAssessment(response.data.assessment);
      } catch (error) {
        localStorage.removeItem('token');
      }
    }
    
    if (adminToken) {
      setAdmin({ token: adminToken });
    }
    
    setLoading(false);
  };

  const login = async (username, password) => {
    const response = await axios.post(`${API}/auth/login`, { username, password });
    const { token, user: userData } = response.data;
    localStorage.setItem('token', token);
    setUser(userData);
    
    // Fetch full user data
    const meResponse = await axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setOrgProfile(meResponse.data.org_profile);
    setAssessment(meResponse.data.assessment);
    
    return userData;
  };

  const adminLogin = async (username, password) => {
    const response = await axios.post(`${API}/admin/login`, { username, password });
    const { token, admin: adminData } = response.data;
    localStorage.setItem('adminToken', token);
    setAdmin({ ...adminData, token });
    return adminData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setOrgProfile(null);
    setAssessment(null);
  };

  const adminLogout = () => {
    localStorage.removeItem('adminToken');
    setAdmin(null);
  };

  const updateOrgProfile = (profile) => {
    setOrgProfile(profile);
  };

  const updateAssessment = (assessmentData) => {
    setAssessment(assessmentData);
  };

  const refreshUser = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data.user);
      setOrgProfile(response.data.org_profile);
      setAssessment(response.data.assessment);
    }
  };

  const getToken = () => localStorage.getItem('token');
  const getAdminToken = () => localStorage.getItem('adminToken');

  return (
    <AuthContext.Provider value={{
      user,
      admin,
      loading,
      orgProfile,
      assessment,
      login,
      adminLogin,
      logout,
      adminLogout,
      updateOrgProfile,
      updateAssessment,
      refreshUser,
      getToken,
      getAdminToken
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
